import { isUserLoggedIn } from "../UIController/user";
import { showAlert } from "../Util/alerts";
import { hasClassAtDepth, isLocal } from "../Util/helper";

let blockedRequests = {};
let globalRequestBlock = false;

// Function to show a spinner on an element to indicate loading
export function showSpinner(elements, request = null, silent, override) {
    if (!Array.isArray(elements)) {
        elements = [elements];
    }
    const hideSpinnerFunctions = [];

    if (globalRequestBlock && (request && blockedRequests[request]) && !override) {
        if (silent != true) {   
            showAlert(t('errors.ongoingRequest'), 'warning');
        }
        return false;
    }

    globalRequestBlock = true;
    if (request) {
        blockedRequests[request] = true;
    }
    elements.forEach(element => {
        if (typeof element === 'string') {
            element = document.getElementById(element);
        }

        if (!element) {
            hideSpinnerFunctions.push(function hideSpinner() {
                console.error('The element for the spinner was not defined.');
            });
            return;
        }
        let originalContent;
        let wasHidden = false;
        if (hasClassAtDepth(element, 2, 'skeleton')) {
            if (element.classList.contains('hidden')) {
                element.classList.remove('hidden')
                wasHidden = true;
            }
        } else {
            originalContent = element.innerHTML;
            const originalWidth = element.offsetWidth;
            const originalHeight = element.offsetHeight;

            element.innerHTML = '<div class="loading loading-ring loading-lg"></div>';
            element.style.width = `${originalWidth}px`;
            element.style.height = `${originalHeight}px`;
        }

        hideSpinnerFunctions.push(function hideSpinner() {
            if (element.classList.contains('skeleton')) {
                if (wasHidden) {
                    element.classList.add('hidden')
                }
            } else {
                element.innerHTML = originalContent;
                element.style.width = '';
                element.style.height = '';
            }
        });
    });

    return function hideAllSpinners() {
        hideSpinnerFunctions.forEach(hideSpinner => hideSpinner());
        globalRequestBlock = false;
        if (request) {
            blockedRequests[request] = false;
        }
    };
}

export function simpleDeleteRequest(url, spinnerId, callback) {
    simpleDeleteRequestWithBody(url, null, spinnerId, callback);
}

export function simpleDeleteRequestWithBody(url, data, spinnerId, callback) {
    fetchRequest('DELETE', url, data, null, spinnerId, callback);
}

export function simpleGetRequest(url, spinnerId, callback) {
    simpleFetchRequest('GET', url, spinnerId, callback);
}

export function simpleGetRequestWithBody(url, data, spinnerId, callback) {
    fetchRequest('GET', url, data, null, spinnerId, callback);
}

export function simplePutRequest(url, spinnerId, callback) {
    simplePutRequestWithBody(url, null, spinnerId, callback);
}

export function simplePutRequestWithBody(url, data, spinnerId, callback) {
    fetchRequest('PUT', url, data, null, spinnerId, callback);
}

export function simplePostRequestWithBody(url, data, spinnerId, callback) {
    fetchRequest('POST', url, data, null, spinnerId, callback);
}

export function simplePostRequest(url, spinnerId, callback) {
    simplePostRequestWithBody(url, null, spinnerId, callback);
}

export function simpleFetchRequest(method, url, spinnerId, callback) {
    fetchRequest(method, url, null, null, spinnerId, callback);
}

export function paginatedRequest(url, spinnerId, callback, force, silent) {
    return paginatedRequestWithBody(url, null, spinnerId, callback, force, silent);
}

export const pages = {};
export function paginatedRequestWithBody(url, data, spinnerId, callback, force, silent = false) {
    if (pages[url] === undefined || force) {
        pages[url] = 1;
    }

    if (pages[url] === 0) {
        return false;
    }

    const page = pages[url];
    const finalUrl = url.replaceAll(':page', page);
    data = { ...data, page };
    const headers = { 'Content-Type': 'application/json',
        'data': JSON.stringify(data)
    };
    return fetchRequest('GET', finalUrl, data, headers, spinnerId, response => {
        if (response.error) {
            handleErrors(response);
            return;
        }
        
        const hasMoreData = () => {
            // Check direct array
            if (Array.isArray(response) && response.length > 0) {
                return true;
            }
            
            // Check response.data array
            if (response.data) {
                if (Array.isArray(response.data) && response.data.length > 0) {
                    return true;
                }
                // Check nested arrays in data object
                let array = null
                for (const key in response.data) {
                    if (Array.isArray(response.data[key]) && response.data[key].length > 0) {
                        array = response.data[key];
                        return true;
                    }
                }
                
                if (array && response.data.count && !isNaN(response.data.count)) {
                    if (response.data.count > 0 && response.data.count > array.length) {
                        return true;
                    }
                }
            }
            return true;
        };
        if (hasMoreData()) {
            pages[url]++;
        } else {
            pages[url] = 0;
        }

        callback(response, hasMoreData(), page);
        return true;
    }, silent);
}

export async function fetchRequest(method, url, data, header, spinnerId, callback, silent, override) {
    let spinner = null;
    if (method === null) {
        console.error('The method is not defined.');
        return;
    }
    url = replaceUrlParams(url);
    if (url === null) {
        console.error('The URL is not defined.');
        return;
    }

    if (spinnerId && spinnerId !== '') {
        let spinnerElements = [];

        if (Array.isArray(spinnerId) && spinnerId.length == 0) {
            console.error('Spinner Ids are empty!')
            return;
        } else if (Array.isArray(spinnerId)) {
            if (typeof spinnerId[0] === 'string') {
                spinnerElements = spinnerId.map(id => document.getElementById(id));
            } else {
                spinnerElements = spinnerId;
            }
        } else {
            if (typeof spinnerId === 'string') {
                spinnerElements = [document.getElementById(spinnerId)];
            } else {
                spinnerElements = [spinnerId];
            }
        }

        if (spinnerElements.length === 0 || spinnerElements.includes(null)) {
            console.error('Spinner Elements were not found for:', spinnerId);
            return;
        }

        spinner = showSpinner(spinnerElements, url, silent, override);

        if (spinner === false) {
            return;
        }
    }

    let headers = {};

    if (header) {
        headers = header;
    }

    let body = null;
    if (method !== 'GET' && data) {
        if (data instanceof FormData) {
            body = data;
            // Do not set Content-Type header for FormData
        } else {
            headers['Content-Type'] = 'application/json';
            body = JSON.stringify(data);
        }
    }

    if (method === 'GET' && data) {
        if (url.includes('?')) {
            url += `&${new URLSearchParams(data).toString()}`;
        } else {
            url += `?${new URLSearchParams(data).toString()}`;
        }
    }

    if (isLocal()) {
        console.log('Request:', method, url);
    }

    fetch(url, {
        method: method,
        headers: headers,
        body: body,
    }).then(response => {
        if (response.body) {
            return response.json();
        }

        showAlert(t('errors.noBody'), 'error');
        return null;
    }).then(response => {
        if (spinner) {
            spinner();
        }

        if (response === null) {
            return;
        }

        if (isLocal()) {
            console.log('Response:', response);
        }

        if (response.error && !callback) {
            handleErrors(response, silent);
            return;
        }

        if (callback) {
            return callback(response);
        } else {
            if (response.message) {
                showAlert(response.message, 'success');
            } else if (!silent) {
                showAlert(t('success.requestSuccessful'), 'success');
            }
        }
    }).catch(error => {
        if (spinner) {
            spinner();
        }
        console.error(`Something went wrong with your ${method} Request!`, error);
    });
}

function replaceUrlParams(url) {
    if (!url) {
        return null;
    }

    if (url.includes(':username') || url.includes(':user')) {
        if (isUserLoggedIn()) {
            url = url.replaceAll(':username', alertUser.username).replaceAll(':user', alertUser.username);
        } else {
            showAlert(t('errors.unauthorized'), 'error');
            return null;
        }
    }

    if (url.includes(':userId') || url.includes(':id')) {
        if (isUserLoggedIn()) {
            url = url.replaceAll(':userId', alertUser.id).replaceAll(':id', alertUser.id);
        } else {
            showAlert(t('errors.unauthorized'), 'error');
            return null;
        }
    }

    return url;
}

export function handleErrors(response, silent) {
    if (response.message) {
        if (typeof response.message === 'object' && response.message.code && response.message.errno) {
            showAlert(`${response.message.code} (${response.message.errno})`, 'error');
        } else {
            if (response.error && response.message) {
                if (isLocal()) {
                showAlert(response.message + " [" + response.error + "]", 'error');
                } else {
                    showAlert(response.message, 'error');
                }
            } else {
                showAlert(response.message, 'error');
            }
        }

        if (response.error) {
            console.error(response.error + " Error:", response.message);
        } else {
            console.error(response.message);
        }
        return;
    }

    console.error('An error occurred:', response.error);

    switch (response.error) {
        case "unknown":
            showAlert(t('errors.default'), 'error');
            break;
        case "notFound":
            showAlert(t('errors.contentNotFound'), 'error');
            break;
        case "invalidInput":
            showAlert(t('errors.invalidInput'), 'error');
            break;
        case "invalidDuplicate":
            showAlert(t('errors.invalidDuplicate'), 'error');
            break;
        case "limitReached":
            showAlert(t('errors.limitReached'), 'error');
            break;
        case "none":
            if (!silent) {
                showAlert(t('errors.none'), 'error');
            }
            break;
        case "notImplemented":
            showAlert(t('errors.notImplemented'), 'error');
            break;
        case "unauthorized":
            showAlert(t('errors.unauthorized'), 'error');
            break;
        case "loggedIn":
            showAlert(t('errors.unauthorized'), 'error');
            break;
        case "acceptTerms":
            showAlert(t('errors.acceptTerms'), 'error');
            break;
        case "uploadFailed":
            showAlert(t('errors.uploadFailed'), 'error');
            break;
        case "tooSoon":
            showAlert(t('errors.tooSoon'), 'error');
            break;
    }

    if (response.error === 'none' && !silent) {
        showAlert(t('errors.none'), 'error');
        return;
    }

    showAlert(response.error, 'error');
    return;
}